// client/src/voice/VoiceContext.js

import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { routeIntent } from "./intentRouter";
import { buildSherlocSystemPrompt } from "./sherlocSystemPrompt";
import { useAdvertiser } from "../contexts/AdvertiserContext";
import { useAuth } from "../contexts/AuthContext";
import { speakWithPremiumVoice } from "./realtimeClient";

// ✅ Eden API client (returns normalized contract)
import { EDEN_SESSION_UNCONFIRMED_REPLY, edenAsk, resetEdenConversationState } from "../eden/core/edenClient";
// Eden session boundary: the signed-in account that owns this page's Eden state.
import {
  EDEN_SESSION_TOKEN_KEYS,
  edenSessionBearer,
  getEdenSessionGeneration,
  isCurrentEdenSession,
  readLiveSessionToken,
  resolveEdenSessionAccount,
  subscribeEdenSession,
  syncEdenSessionAccount,
} from "../eden/core/edenSession";
// Governed planner change proposed in chat (validated before display) and the
// schedule-edit shape local routing must not swallow.
import { normalizeEdenGovernedChange } from "../eden/core/edenGovernedChange";
import {
  listedCampaignNames,
  looksLikeCampaignScheduleEditRequest,
  looksLikeCampaignScheduleQuestion,
  looksLikeLocalCommand,
  withoutNavigationPhrase,
} from "../eden/core/edenScheduleEditIntent";
import { EDEN_ACTION_TYPES, resolveEdenActionIntent } from "../config/edenActionRegistry";

// ✅ Eden intent inference (used only for SHERLOC_CHAT)
import { inferIntent as inferEdenIntent } from "../eden/core/intentRouter";

// ✅ Sherloc Media Planner intake assist (deterministic, network-free)
import {
  isPlannerRoute,
  looksLikePlannerIntake,
  buildPlannerDraftDetail,
  EDEN_PLANNER_DRAFT_EVENT,
} from "../eden/planner/plannerIntakeAssist";

// ✅ Sherloc Media Planner conversational revision routing
import {
  PLANNER_ROUTE as PLANNER_REVISION_ROUTE,
  detectPlannerRevisionIntent,
  resolvePlannerRevisionTarget,
  matchClarificationChoice,
  buildPlannerRevisionNavState,
  revisionSuccessCopy,
  revisionClarifyCopy,
  REVISION_NOT_FOUND_COPY,
  fetchPlannerRevisionCampaigns,
} from "../eden/planner/plannerRevisionRouting";

// Does "<rest>" name a page the action registry would open?
function namesAPage(rest) {
  const action = resolveEdenActionIntent(`open ${rest}`);
  return Boolean(action && action.type !== EDEN_ACTION_TYPES.ANALYSIS_FALLBACK);
}

const VoiceContext = createContext(null);

// Optional hard fallback from env:
// In client/.env: REACT_APP_DEFAULT_FIRST_NAME=David
const ENV_FALLBACK_NAME = process.env.REACT_APP_DEFAULT_FIRST_NAME || null;
// Turns kept for the in-app conversation view (oldest dropped first).
const MAX_CONVERSATION_TURNS = 80;
// edenAsk aborts at EDEN_ASK_TIMEOUT_MS and reports the timeout; the request
// watchdog is only a backstop past that, so it never ends a request that is
// still in flight or throws away a slow reply.
const EDEN_ASK_TIMEOUT_MS = 45000;
const EDEN_ASK_WATCHDOG_GRACE_MS = 5000;

const PLANNER_REVISION_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_BASE_URL || "";

const DISPLAY_NAME_STORAGE_KEYS = [
  "preferredDisplayName",
  "preferred_display_name",
  "preferredName",
  "preferred_name",
  "displayName",
  "display_name",
  "nickname",
  "nick_name",
  "firstName",
  "first_name",
];

const isEmailLike = (value) =>
  typeof value === "string" && value.includes("@");

const normalizePersonName = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || isEmailLike(trimmed)) return null;

  const firstToken = trimmed
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean)[0];

  if (!firstToken) return null;
  if (!/[A-Za-z]/.test(firstToken)) return null;

  const cleaned = firstToken.replace(/^[^A-Za-z]+|[^A-Za-z'-]+$/g, "");
  if (!cleaned) return null;

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const getStoredDisplayName = () => {
  if (typeof window === "undefined" || !window.localStorage) return null;

  for (const key of DISPLAY_NAME_STORAGE_KEYS) {
    const candidate = normalizePersonName(window.localStorage.getItem(key));
    if (candidate) return candidate;
  }

  return null;
};

/**
 * Try to find a reasonable display name from the auth object.
 * 1) Prefer explicit preferred display name / nickname fields
 * 2) Then use first-name style fields
 * 3) Then use the first token of a full human name
 * Never derive from email.
 */
function resolveDisplayNameFromAuth(auth) {
  if (!auth || typeof auth !== "object") return null;

  const user = auth?.user || auth?.authState?.user || null;
  const scanTargets = [user, auth?.authState, auth];

  try {
    if (user && typeof user === "object") {
      const preferredCandidate = [
        user.preferredDisplayName,
        user.preferred_display_name,
        user.preferredName,
        user.preferred_name,
        user.displayName,
        user.display_name,
        user.nickname,
        user.nick_name,
      ]
        .map(normalizePersonName)
        .find(Boolean);

      if (preferredCandidate) return preferredCandidate;

      const firstNameCandidate = [
        user.firstName,
        user.first_name,
        user.givenName,
        user.given_name,
      ]
        .map(normalizePersonName)
        .find(Boolean);

      if (firstNameCandidate) return firstNameCandidate;

      const fullNameCandidate = [user.name, user.fullName, user.full_name]
        .map(normalizePersonName)
        .find(Boolean);

      if (fullNameCandidate) return fullNameCandidate;
    }

    for (const target of scanTargets) {
      if (!target || typeof target !== "object") continue;
      const targetLevelCandidate = [
        target.preferredDisplayName,
        target.preferred_display_name,
        target.preferredName,
        target.preferred_name,
        target.displayName,
        target.display_name,
        target.nickname,
        target.nick_name,
        target.firstName,
        target.first_name,
        target.givenName,
        target.given_name,
        target.name,
        target.fullName,
        target.full_name,
      ]
        .map(normalizePersonName)
        .find(Boolean);

      if (targetLevelCandidate) return targetLevelCandidate;
    }
  } catch {
    // ignore
  }

  const nameFieldPriority = [
    "preferreddisplayname",
    "preferredname",
    "displayname",
    "nickname",
    "firstname",
    "givenname",
    "fullname",
    "name",
  ];

  const foundNamesByKey = new Map();
  const visited = new Set();

  function scan(obj, depth) {
    if (!obj || typeof obj !== "object" || depth > 4 || visited.has(obj)) return;
    visited.add(obj);

    for (const [key, value] of Object.entries(obj)) {
      if (value == null) continue;

      if (typeof value === "string") {
        const lowerKey = key.toLowerCase().replace(/[\s_-]/g, "");
        const normalizedValue = normalizePersonName(value);
        if (!normalizedValue) continue;

        if (
          lowerKey.includes("preferred") ||
          lowerKey.includes("display") ||
          lowerKey.includes("nickname") ||
          lowerKey.includes("first") ||
          lowerKey.includes("given") ||
          lowerKey.includes("full") ||
          lowerKey === "name"
        ) {
          if (!foundNamesByKey.has(lowerKey)) {
            foundNamesByKey.set(lowerKey, normalizedValue);
          }
        }
      } else if (typeof value === "object") {
        scan(value, depth + 1);
      }
    }
  }

  scan(auth?.authState, 0);
  scan(auth, 0);

  for (const key of nameFieldPriority) {
    if (foundNamesByKey.has(key)) return foundNamesByKey.get(key);
  }

  return null;
}

/**
 * Safety gate for voice navigation:
 * - internal route only (starts with "/")
 * - no "http"
 */
function isSafeInternalPath(p) {
  if (!p || typeof p !== "string") return false;
  if (!p.startsWith("/")) return false;
  if (p.toLowerCase().includes("http")) return false;
  return true;
}

export function SherlocVoiceProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const advertiserCtx = useAdvertiser() || {};
  const auth = useAuth();
  // The auth context's confirmed sign-in and the identity it verified with the
  // server: the Eden session may act only for that account (edenSession.js).
  const authSignedIn = Boolean(auth?.isAuthenticated);
  const authUserId = auth?.user?.id ?? null;
  const authEmail = auth?.email || auth?.user?.email || "";
  const checkAuthentication = auth?.checkAuthentication;

  // ✅ Eden context injected by GlobalVoiceController
  const [edenContext, setEdenContext] = useState(null);

  const firstName = useMemo(() => {
    const storedName = getStoredDisplayName();
    const fromAuth = resolveDisplayNameFromAuth(auth);
    return storedName || fromAuth || normalizePersonName(ENV_FALLBACK_NAME) || null;
  }, [auth]);

  const role = "client";

  const [isListening, setIsListening] = useState(false);
  const [activeRequestCount, setActiveRequestCount] = useState(0);
  // Requests Eden is answering whose reply has not arrived yet. The working
  // indicator shows only while this is above zero: not while a reply that
  // has arrived is being spoken, and not after the request ends.
  const [awaitingReplyCount, setAwaitingReplyCount] = useState(0);
  const [isThinking, setIsThinking] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [dictationMode, setDictationMode] = useState(false);
  const [voicePhase, setVoicePhase] = useState("idle");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [voiceOutputStatus, setVoiceOutputStatus] = useState({
    status: "idle",
    reason: null,
    path: null,
  });

  const [lastUserUtterance, setLastUserUtteranceState] = useState("");
  const [lastAssistantReply, setLastAssistantReplyState] = useState("");
  // One conversation for every Eden surface (entry, floating panel, full
  // screen, compact preview): an append-only list of what was said, so
  // reopening or expanding the chat shows the same thread instead of only
  // the last exchange. Display state only; nothing here is sent anywhere.
  const [conversationTurns, setConversationTurns] = useState([]);
  const conversationTurnSequenceRef = useRef(0);
  // The Eden turn that asked the person a question ("Which campaign do you
  // mean?"), while it is still the latest turn: a short reply to it is an
  // answer for Eden, not a page shortcut.
  const edenQuestionTurnRef = useRef(null);
  // The campaigns that question listed, if it listed any.
  const edenQuestionNamesRef = useRef([]);
  const appendConversationTurn = useCallback((role, text) => {
    const clean = String(text || "").trim();
    if (!clean) return;
    conversationTurnSequenceRef.current += 1;
    const turn = { id: `turn-${conversationTurnSequenceRef.current}`, role, text: clean };
    setConversationTurns((turns) => [...turns, turn].slice(-MAX_CONVERSATION_TURNS));
  }, []);
  const setLastUserUtterance = useCallback(
    (value) => {
      setLastUserUtteranceState(value);
      if (typeof value === "string") appendConversationTurn("user", value);
    },
    [appendConversationTurn]
  );
  const setLastAssistantReply = useCallback(
    (value) => {
      setLastAssistantReplyState(value);
      if (typeof value === "string") appendConversationTurn("eden", value);
    },
    [appendConversationTurn]
  );
  // The latest reply's governed change proposal (validated), or null.
  const [lastGovernedChange, setLastGovernedChange] = useState(null);
  const [lastAssistantVisualizations, setLastAssistantVisualizations] = useState([]);
  // Delegated media-plan drafting: the durable backend work request attached
  // to this conversation (acknowledgment card). The row on the server is the
  // authoritative job — this is display state only.
  const [delegatedWork, setDelegatedWork] = useState(null);
  const speechUtteranceRef = useRef(null);
  const premiumAudioRef = useRef(null);
  const speechGenerationRef = useRef(0);
  const speechMountedRef = useRef(true);
  const activeRequestRegistryRef = useRef(new Map());
  const requestSequenceRef = useRef(0);
  // Advances when the Eden session ends (sign-out, session loss, another
  // account); the controller remounts on it so its own state goes too.
  const [edenSessionGeneration, setEdenSessionGeneration] = useState(() => getEdenSessionGeneration());
  const verificationRequestedForRef = useRef(null);
  // Pending Sherloc planner-revision clarification ("Which campaign?") options.
  const pendingPlannerRevisionRef = useRef(null);
  const isSending = activeRequestCount > 0;
  const isProcessing = isSending;

  const chooseBrowserFallbackVoice = useCallback((voicesOverride = null) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return null;
    const voices = Array.isArray(voicesOverride)
      ? voicesOverride
      : window.speechSynthesis.getVoices?.() || [];
    if (!voices.length) return null;

    const preferredPatterns = [
      /google uk english female/i,
      /\bnova\b/i,
      /\bserena\b/i,
      /\bava\b/i,
      /\bsamantha\b/i,
      /\bvictoria\b/i,
      /\bkaren\b/i,
      /\bfemale\b/i,
    ];

    for (const pattern of preferredPatterns) {
      const match = voices.find((voice) => pattern.test(`${voice.name} ${voice.voiceURI}`));
      if (match) return match;
    }

    return (
      voices.find((voice) => String(voice.lang || "").toLowerCase().startsWith("en-gb")) ||
      voices.find((voice) => String(voice.lang || "").toLowerCase().startsWith("en")) ||
      voices[0]
    );
  }, []);

  const waitForBrowserVoices = useCallback((timeoutMs = 1500) => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      return Promise.resolve([]);
    }

    const synth = window.speechSynthesis;
    const existingVoices = synth.getVoices?.() || [];
    if (existingVoices.length) {
      return Promise.resolve(existingVoices);
    }

    return new Promise((resolve) => {
      let settled = false;
      let timeoutId = 0;

      const finish = (voices = []) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        if (typeof synth.removeEventListener === "function") {
          synth.removeEventListener("voiceschanged", handleVoicesChanged);
        } else if (synth.onvoiceschanged === handleVoicesChanged) {
          synth.onvoiceschanged = null;
        }
        resolve(Array.isArray(voices) ? voices : []);
      };

      const handleVoicesChanged = () => {
        const nextVoices = synth.getVoices?.() || [];
        if (nextVoices.length) {
          finish(nextVoices);
        }
      };

      if (typeof synth.addEventListener === "function") {
        synth.addEventListener("voiceschanged", handleVoicesChanged);
      } else {
        synth.onvoiceschanged = handleVoicesChanged;
      }

      timeoutId = window.setTimeout(() => {
        finish(synth.getVoices?.() || []);
      }, timeoutMs);
    });
  }, []);

  const clearInterimTranscript = useCallback(() => {
    setInterimTranscript("");
  }, []);

  const clearVoiceOutputStatus = useCallback(() => {
    setVoiceOutputStatus({
      status: "idle",
      reason: null,
      path: null,
    });
  }, []);

  const refreshRequestUiState = useCallback(() => {
    const nextCount = activeRequestRegistryRef.current.size;
    const hasActive = nextCount > 0;
    setActiveRequestCount(nextCount);
    setAwaitingReplyCount(
      [...activeRequestRegistryRef.current.values()].filter((entry) => entry?.awaitingReply).length
    );
    setIsThinking(hasActive);
    setVoicePhase((prev) => {
      if (hasActive) return prev === "speaking" ? prev : "processing";
      return prev === "processing" ? "idle" : prev;
    });
  }, []);

  const beginEdenRequest = useCallback(() => {
    const requestId = requestSequenceRef.current + 1;
    requestSequenceRef.current = requestId;

    const timeoutId = window.setTimeout(() => {
      const entry = activeRequestRegistryRef.current.get(requestId);
      if (!entry) return;
      activeRequestRegistryRef.current.delete(requestId);
      refreshRequestUiState();
      console.warn("[SherlocVoice][Client]", "request_watchdog_reset", { requestId });
      // Said only if no reply came: a reply that arrived is already on screen.
      if (entry.awaitingReply) setLastAssistantReply("Eden's reply didn't arrive. You can ask again.");
      setLastAssistantVisualizations([]);
    }, EDEN_ASK_TIMEOUT_MS + EDEN_ASK_WATCHDOG_GRACE_MS);

    setLastAssistantVisualizations([]);
    activeRequestRegistryRef.current.set(requestId, { timeoutId, awaitingReply: true });
    refreshRequestUiState();
    return requestId;
  }, [refreshRequestUiState, setLastAssistantReply]);

  // The reply (or its error) for this request has arrived.
  const markEdenReplyReceived = useCallback(
    (requestId) => {
      const entry = activeRequestRegistryRef.current.get(requestId);
      if (!entry || !entry.awaitingReply) return;
      entry.awaitingReply = false;
      refreshRequestUiState();
    },
    [refreshRequestUiState]
  );

  const finishEdenRequest = useCallback(
    (requestId) => {
      const activeRequest = activeRequestRegistryRef.current.get(requestId);
      if (!activeRequest) return false;
      window.clearTimeout(activeRequest.timeoutId);
      activeRequestRegistryRef.current.delete(requestId);
      refreshRequestUiState();
      return true;
    },
    [refreshRequestUiState]
  );

  useEffect(() => {
    const activeRequestRegistry = activeRequestRegistryRef.current;
    speechMountedRef.current = true;
    return () => {
      speechMountedRef.current = false;
      speechGenerationRef.current += 1;
      try { premiumAudioRef.current?.pause(); } catch {}
      try { window.speechSynthesis?.cancel(); } catch {}
      activeRequestRegistry.forEach((entry) => {
        window.clearTimeout(entry?.timeoutId);
      });
      activeRequestRegistry.clear();
    };
  }, []);

  // ---- Eden session boundary ---------------------------------------------
  // The conversation, last exchange, pending governed change, delegated work,
  // injected context and carried conversation state belong to ONE signed-in
  // account. When the Eden session ends they are cleared: requests still in
  // flight are dropped when they return (their registry entries are gone),
  // speech stops, and the controller remounts with a fresh draft and panel.
  const resetAccountOwnedEdenState = useCallback(() => {
    activeRequestRegistryRef.current.forEach((entry) => {
      window.clearTimeout(entry?.timeoutId);
    });
    activeRequestRegistryRef.current.clear();
    refreshRequestUiState();
    speechGenerationRef.current += 1;
    try { premiumAudioRef.current?.pause(); } catch {}
    premiumAudioRef.current = null;
    speechUtteranceRef.current = null;
    try { window.speechSynthesis?.cancel(); } catch {}
    setIsSpeaking(false);
    setIsListening(false);
    pendingPlannerRevisionRef.current = null;
    edenQuestionTurnRef.current = null;
    edenQuestionNamesRef.current = [];
    resetEdenConversationState();
    setEdenContext(null);
    setConversationTurns([]);
    setLastUserUtteranceState("");
    setLastAssistantReplyState("");
    setLastGovernedChange(null);
    setLastAssistantVisualizations([]);
    setDelegatedWork(null);
    setInterimTranscript("");
    setDictationMode(false);
    setVoicePhase("idle");
    setVoiceOutputStatus({ status: "idle", reason: null, path: null });
    setEdenSessionGeneration(getEdenSessionGeneration());
  }, [refreshRequestUiState]);

  // Bind the Eden session to the account the auth context confirms. A live
  // token naming a different account than the one the auth context verified
  // (another tab signed in), or an auth context that does not yet know who is
  // signed in, fails closed (no bearer) until the auth context has verified
  // the stored token. An explicit send asks for that verification again.
  const evaluateEdenSession = useCallback(({ forceVerification = false } = {}) => {
    const { account, reason } = resolveEdenSessionAccount({
      signedIn: authSignedIn,
      userId: authUserId,
      email: authEmail,
    });
    syncEdenSessionAccount(account);
    if (
      (reason === "auth_state_differs" || reason === "auth_identity_unknown") &&
      typeof checkAuthentication === "function"
    ) {
      const liveToken = readLiveSessionToken();
      if (liveToken && (forceVerification || verificationRequestedForRef.current !== liveToken)) {
        verificationRequestedForRef.current = liveToken;
        Promise.resolve()
          .then(() => checkAuthentication())
          .catch(() => {});
      }
    }
    return Boolean(account);
  }, [authSignedIn, authUserId, authEmail, checkAuthentication]);

  useEffect(() => subscribeEdenSession(resetAccountOwnedEdenState), [resetAccountOwnedEdenState]);

  useEffect(() => {
    evaluateEdenSession();
  }, [evaluateEdenSession, edenSessionGeneration]);

  // Another tab signing in or out changes the stored token without touching
  // this page's auth context; re-check the binding when that happens.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleStorage = (event) => {
      if (event.key === null || EDEN_SESSION_TOKEN_KEYS.includes(event.key)) evaluateEdenSession();
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [evaluateEdenSession]);

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    console.log("Eden State:", {
      isListening,
      isSending,
      isThinking,
    });
  }, [isListening, isSending, isThinking]);

  const stopSpeaking = useCallback(() => {
    speechGenerationRef.current += 1;
    try {
      if (premiumAudioRef.current) {
        premiumAudioRef.current.pause();
        premiumAudioRef.current.currentTime = 0;
      }
    } catch {}
    premiumAudioRef.current = null;
    try {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    } catch {}
    speechUtteranceRef.current = null;
    setIsSpeaking(false);
    setVoicePhase((prev) => (prev === "speaking" ? "idle" : prev));
    setVoiceOutputStatus({
      status: "idle",
      reason: null,
      path: null,
    });
  }, []);

  const speakAssistantReply = useCallback(async (text, speechAuthorization) => {
    const generation = ++speechGenerationRef.current;
    const isCurrent = () => speechMountedRef.current && generation === speechGenerationRef.current;
    const spoken = String(text || "").trim();
    if (!spoken) {
      setVoicePhase("idle");
      setVoiceOutputStatus({
        status: "idle",
        reason: "missing_text",
        path: null,
      });
      return false;
    }

    setVoiceOutputStatus({
      status: "starting",
      reason: null,
      path: "premium_provider",
    });

    const premiumResult = await speakWithPremiumVoice({
      text: spoken,
      speechAuthorization,
      isCurrent,
      portal: "client",
      purpose: "reply",
      onStart: (audio) => {
        console.log("[SherlocVoice][Client]", "tts_started", { path: "premium_provider" });
        premiumAudioRef.current = audio || null;
        setIsSpeaking(true);
        setVoicePhase("speaking");
        setVoiceOutputStatus({
          status: "speaking",
          reason: null,
          path: "premium_provider",
        });
      },
      onEnd: () => {
        console.log("[SherlocVoice][Client]", "tts_ended", { path: "premium_provider" });
        premiumAudioRef.current = null;
        setIsSpeaking(false);
        setVoicePhase("idle");
        setVoiceOutputStatus({
          status: "played",
          reason: null,
          path: "premium_provider",
        });
      },
      onError: (error) => {
        console.warn("[SherlocVoice][Client]", "tts_failed", {
          path: "premium_provider",
          error: error?.message || error?.error || "unknown",
        });
        setVoiceOutputStatus({
          status: "failed",
          reason: error?.message || error?.error || "premium_provider_failed",
          path: "premium_provider",
        });
      },
    });

    if (!isCurrent()) return false;
    if (premiumResult.ok) {
      return true;
    }
    if (premiumResult.browserFallbackAllowed !== true) {
      setVoicePhase("idle");
      setVoiceOutputStatus({ status: "withheld", reason: premiumResult.code || premiumResult.reason, path: null });
      return false;
    }

    console.warn("[SherlocVoice][Client]", "tts_fallback_browser", {
      reason: premiumResult.reason || "premium_unavailable",
    });
    setVoiceOutputStatus({
      status: "fallback_pending",
      reason: premiumResult.reason || "premium_unavailable",
      path: "browser_fallback",
    });

    if (typeof window === "undefined" || !window.speechSynthesis) {
      setVoicePhase("idle");
      setVoiceOutputStatus({
        status: "failed",
        reason: "speech_synthesis_unavailable",
        path: "browser_fallback",
      });
      return false;
    }

    const fallbackVoices = await waitForBrowserVoices();
    if (!isCurrent()) return false;
    if (!fallbackVoices.length) {
      console.warn("[SherlocVoice][Client]", "browser_voices_not_loaded", {
        reason: "voices_empty_after_wait",
      });
    }

    return new Promise((resolve) => {
      try {
        window.speechSynthesis.cancel();
      } catch {}

      const utterance = new window.SpeechSynthesisUtterance(spoken);
      utterance.voice = chooseBrowserFallbackVoice(fallbackVoices);
      utterance.lang = utterance.voice?.lang || "en-GB";
      utterance.rate = 1.01;
      utterance.pitch = 1;
      utterance.volume = 1;
      utterance.onstart = () => {
        console.log("[SherlocVoice][Client]", "tts_started", {
          path: "browser_fallback",
          voice: utterance.voice?.name || "default",
        });
        setIsSpeaking(true);
        setVoicePhase("speaking");
        setVoiceOutputStatus({
          status: "speaking",
          reason: null,
          path: "browser_fallback",
        });
      };
      utterance.onend = () => {
        console.log("[SherlocVoice][Client]", "tts_ended", { path: "browser_fallback" });
        speechUtteranceRef.current = null;
        setIsSpeaking(false);
        setVoicePhase("idle");
        setVoiceOutputStatus({
          status: "played",
          reason: null,
          path: "browser_fallback",
        });
        resolve(true);
      };
      utterance.onerror = (event) => {
        console.warn("[SherlocVoice][Client]", "tts_failed", {
          path: "browser_fallback",
          error: event?.error || "unknown",
        });
        speechUtteranceRef.current = null;
        setIsSpeaking(false);
        setVoicePhase("idle");
        setVoiceOutputStatus({
          status: "failed",
          reason: event?.error || "browser_fallback_failed",
          path: "browser_fallback",
        });
        resolve(false);
      };

      speechUtteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    });
  }, [chooseBrowserFallbackVoice, waitForBrowserVoices]);

  // ✅ advertiserName compatibility (client context exposes both legacy + canonical)
  const selectedAdvertiserName =
    advertiserCtx.selectedAdvertiserName ||
    advertiserCtx.selectedAdvertiser ||
    advertiserCtx.selectedAdvertiser?.advertiser ||
    advertiserCtx.selectedAdvertiser?.name ||
    null;

  const selectedAdvertiserId =
    advertiserCtx.selectedAdvertiserId ||
    advertiserCtx.selectedAdvertiser?.id ||
    null;
  const selectedAdvertiserVertical =
    advertiserCtx.selectedAdvertiserVertical ||
    advertiserCtx.selectedAdvertiser?.vertical ||
    "general";

  // ✅ Prefer Eden context if present (otherwise fall back)
  const effectiveAdvertiserName =
    edenContext?.advertiser?.name || selectedAdvertiserName || null;

  const effectivePath = edenContext?.route || location.pathname;

  const sherlocSystemPrompt = useMemo(
    () =>
      buildSherlocSystemPrompt({
        role,
        advertiserName: effectiveAdvertiserName,
        currentPath: effectivePath,
      }),
    [role, effectiveAdvertiserName, effectivePath]
  );

  const sendToSherloc = useCallback(
    async (userText, intentHint = null, options = {}) => {
      // ✅ Sherloc Media Planner intake assist.
      // When a user pastes rough client/prospect intake while on the planner
      // page, structure it locally into an editable planner draft and hand it to
      // the page via an event. This is deterministic and network-free, so it does
      // not depend on the heavier /api/eden/ask governance round-trip (the path
      // that was failing on the planner handoff). Eden only drafts here — it never
      // saves, launches, deploys, or spends.
      const activePath = edenContext?.route || location.pathname;
      if (!dictationMode && isPlannerRoute(activePath) && looksLikePlannerIntake(userText)) {
        try {
          /*
           * Cortex is the client portal: Eden must never draft, name or
           * retain the internal net-media budget or target margin here.
           */
          const detail = buildPlannerDraftDetail(userText, { clientScoped: true });
          if (typeof window !== "undefined" && window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent(EDEN_PLANNER_DRAFT_EVENT, { detail }));
          }
          setLastAssistantReply(detail.summary);
          setLastAssistantVisualizations([]);
          setVoicePhase("ready");
        } catch (err) {
          console.error("[SherlocVoice][Client] planner intake assist failed:", err);
          setLastAssistantReply(
            "I couldn't structure that into a planner draft just now. You can paste the details again or fill the fields manually — nothing was changed."
          );
          setLastAssistantVisualizations([]);
          setVoicePhase("error");
        }
        return;
      }

      // Re-check the binding before anything is sent; the reply below lands only
      // while this Eden session is still current.
      evaluateEdenSession({ forceVerification: true });
      const sessionGeneration = getEdenSessionGeneration();
      const requestId = beginEdenRequest();
      try {
        // ✅ Build best available context (prefer Eden)
        const ctx =
          edenContext || {
            page: null,
            route: location.pathname,
            role,
            advertiser: {
              id: selectedAdvertiserId || null,
              name: selectedAdvertiserName || null,
              vertical: selectedAdvertiserVertical,
            },
            dateRange: null,
            auth: {
              hasToken: Boolean(
                localStorage.getItem("clientAuthToken") ||
                  localStorage.getItem("authToken")
              ),
              token:
                localStorage.getItem("clientAuthToken") ||
                localStorage.getItem("authToken"),
            },
          };

        // ✅ Eden intent inference for better backend routing
        const inferred = inferEdenIntent(userText, ctx);
        const edenIntent = intentHint || inferred;

        const res = await edenAsk({
          message: userText,
          context: ctx,
          meta: {
            channel: "voice",
            systemPromptVersion: "v1",
            dictationMode: Boolean(dictationMode),
            entryPoint: options.entryPoint || "conversation",
          },
          intent: edenIntent,
          timeoutMs: EDEN_ASK_TIMEOUT_MS,
        });

        if (!activeRequestRegistryRef.current.has(requestId) || !isCurrentEdenSession(sessionGeneration)) {
          return;
        }
        markEdenReplyReceived(requestId);
        edenQuestionTurnRef.current = null;
        edenQuestionNamesRef.current = [];

        if (!res.ok) {
          setLastAssistantReply(res.error || "Eden is unavailable right now.");
          setLastAssistantVisualizations([]);
          setVoicePhase("error");
          return;
        }

        const reply = res.data?.reply || "No reply provided.";
        setLastAssistantReply(reply);
        if (res.data?.status === "needs_clarification") {
          edenQuestionTurnRef.current = `turn-${conversationTurnSequenceRef.current}`;
          edenQuestionNamesRef.current = listedCampaignNames(reply);
        }
        setLastGovernedChange(normalizeEdenGovernedChange(res.data?.governedChange, { turnId: requestId, sessionGeneration }));
        if (res.data?.delegatedWork && typeof res.data.delegatedWork === "object") {
          setDelegatedWork(res.data.delegatedWork);
        }
        setLastAssistantVisualizations(
          Array.isArray(res.data?.visualizations) ? res.data.visualizations : []
        );
        const didSpeak = await speakAssistantReply(reply, res.data?.speechAuthorization);
        if (!didSpeak) {
          console.warn("[SherlocVoice][Client]", "voice_output_unavailable_after_reply", {
            reason: voiceOutputStatus?.reason || "unknown",
            path: voiceOutputStatus?.path || "unknown",
          });
        }
      } catch (err) {
        if (!isCurrentEdenSession(sessionGeneration)) return;
        console.error("[SherlocVoice][Client] Error sending to Eden:", err);
        setLastAssistantReply("I ran into an error processing that.");
        setLastAssistantVisualizations([]);
        setVoicePhase("error");
      } finally {
        finishEdenRequest(requestId);
      }
    },
    [
      evaluateEdenSession,
      setLastAssistantReply,
      edenContext,
      location.pathname,
      role,
      selectedAdvertiserId,
      selectedAdvertiserName,
      selectedAdvertiserVertical,
      dictationMode,
      beginEdenRequest,
      markEdenReplyReceived,
      finishEdenRequest,
      speakAssistantReply,
      voiceOutputStatus?.path,
      voiceOutputStatus?.reason,
    ]
  );

  // ✅ Conversational Sherloc Media Planner revision routing.
  // Resolves an advertiser/campaign named in chat, asks which campaign when
  // ambiguous, then NAVIGATES to the planner with pre-selection state. It only
  // navigates + pre-selects — never generates, saves, publishes, or activates.
  const routePlannerRevision = useCallback(
    async (text) => {
      // 1) A pending "Which campaign?" clarification takes priority.
      const pending = pendingPlannerRevisionRef.current;
      if (pending?.options?.length) {
        const choice = matchClarificationChoice(text, pending.options);
        if (choice) {
          pendingPlannerRevisionRef.current = null;
          setLastAssistantReply(revisionSuccessCopy(choice));
          setLastAssistantVisualizations([]);
          navigate(PLANNER_REVISION_ROUTE, { state: buildPlannerRevisionNavState(choice) });
          setVoicePhase("ready");
          return true;
        }
        // Unrecognized reply: drop the pending state and fall through so the user
        // is never trapped in clarification.
        pendingPlannerRevisionRef.current = null;
      }

      // 2) New revision intent?
      const { matched, entity } = detectPlannerRevisionIntent(text);
      if (!matched) return false;

      if (!entity) {
        setLastAssistantReply(REVISION_NOT_FOUND_COPY);
        setLastAssistantVisualizations([]);
        setVoicePhase("ready");
        return true;
      }

      let campaigns = [];
      const sessionGeneration = getEdenSessionGeneration();
      try {
        // Only the Eden session's own bearer; without one nothing is sent and
        // the reply says why, instead of "not found".
        const token = edenSessionBearer();
        if (!token) {
          setLastAssistantReply(EDEN_SESSION_UNCONFIRMED_REPLY);
          setVoicePhase("ready");
          return true;
        }
        campaigns = await fetchPlannerRevisionCampaigns({
          baseUrl: PLANNER_REVISION_BASE_URL,
          token,
        });
      } catch {
        campaigns = [];
      }
      // A lookup that returns after its Eden session ended changes nothing.
      if (!isCurrentEdenSession(sessionGeneration)) return true;

      const result = resolvePlannerRevisionTarget(entity, campaigns);

      if (result.status === "campaign") {
        setLastAssistantReply(revisionSuccessCopy(result.match));
        setLastAssistantVisualizations([]);
        navigate(PLANNER_REVISION_ROUTE, { state: buildPlannerRevisionNavState(result.match) });
        setVoicePhase("ready");
        return true;
      }

      if (result.status === "advertiser_multi" || result.status === "multi") {
        pendingPlannerRevisionRef.current = {
          options: result.options,
          entity: result.entity,
          advertiserName: result.advertiserName || "",
        };
        setLastAssistantReply(revisionClarifyCopy(result));
        setLastAssistantVisualizations([]);
        setVoicePhase("ready");
        return true;
      }

      setLastAssistantReply(REVISION_NOT_FOUND_COPY);
      setLastAssistantVisualizations([]);
      setVoicePhase("ready");
      return true;
    },
    [navigate, setLastAssistantReply, setLastAssistantVisualizations, setVoicePhase]
  );

  // A short reply (not itself a question) while Eden's question is still the
  // latest turn answers that question.
  const isAnswerToEdenQuestion = useCallback((value) => {
    const text = String(value || "").trim();
    if (!text || !edenQuestionTurnRef.current) return false;
    if (edenQuestionTurnRef.current !== `turn-${conversationTurnSequenceRef.current}`) return false;
    if (/\?\s*$/.test(text)) return false;
    // Naming one of the campaigns Eden just listed is an answer, even after a
    // navigation phrase or when the name contains a page's name.
    const key = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    const said = [key(text), key(withoutNavigationPhrase(text))];
    if (edenQuestionNamesRef.current.some((name) => said.includes(key(name)))) return true;
    if (looksLikeLocalCommand(text, { resolvesToPage: namesAPage })) return false;
    return !/\?\s*$/.test(text) && text.split(/\s+/).length <= 6;
  }, []);

  const handleFinalTranscript = useCallback(
    async (finalText, transcriptOptions = {}) => {
      const text = String(finalText || "").trim();
      if (!text) return;
      const answersEdenQuestion = isAnswerToEdenQuestion(text);

      clearInterimTranscript();
      setLastUserUtterance(text);
      setLastGovernedChange(null);

      // An answer to Eden's own question, or a question about a campaign's
      // dates, goes to Eden; local page shortcuts ("YouTube" → analytics)
      // would swallow it.
      if (answersEdenQuestion || looksLikeCampaignScheduleQuestion(text)) {
        await sendToSherloc(text, null, transcriptOptions);
        return;
      }

      // ✅ Dictation mode: treat everything as chat
      if (dictationMode) {
        await sendToSherloc(text, "GENERAL", transcriptOptions);
        return;
      }

      // ✅ Conversational planner-revision routing (navigate + pre-select only).
      // Skipped for delegated planning briefs — those must reach the durable
      // backend delegation path, never local navigation.
      // A request to move a campaign's start/end date to a stated date is a
      // governed change the backend resolves; planner-revision navigation
      // ("update … campaign") must not swallow it, for typed, voice and Eden
      // Entry requests alike.
      if (looksLikeCampaignScheduleEditRequest(text)) {
        await sendToSherloc(text, null, transcriptOptions);
        return;
      }

      if (!transcriptOptions.delegationCandidate && (await routePlannerRevision(text))) {
        return;
      }

      const routingContext = {
        currentPath: edenContext?.route || location.pathname,
        advertiserName: edenContext?.advertiser?.name || selectedAdvertiserName || null,
        role: edenContext?.role || role,
        dateRange: edenContext?.dateRange || null,
        advertiserId: edenContext?.advertiser?.id || selectedAdvertiserId || null,
      };

      const intent = routeIntent(text, routingContext);

      switch (intent.type) {
        case "IGNORE":
          return;

        case "NAVIGATE": {
          const target = intent.targetPath;
          if (isSafeInternalPath(target) && target !== location.pathname) {
            navigate(target);
          } else {
            await sendToSherloc(text, "NAVIGATE", transcriptOptions);
          }
          return;
        }

        case "NAVIGATE_BACK":
          navigate(-1);
          return;

        case "ENTER_DICTATION":
          setDictationMode(true);
          setLastAssistantReply("Dictation mode enabled.");
          setLastAssistantVisualizations([]);
          return;

        case "EXIT_DICTATION":
          setDictationMode(false);
          setLastAssistantReply("Dictation mode disabled.");
          setLastAssistantVisualizations([]);
          return;

        case "SHERLOC_CHAT":
        default:
          await sendToSherloc(text, null, transcriptOptions);
          return;
      }
    },
    [
      setLastAssistantReply,
      setLastUserUtterance,
      dictationMode,
      edenContext,
      location.pathname,
      navigate,
      role,
      selectedAdvertiserId,
      selectedAdvertiserName,
      sendToSherloc,
      clearInterimTranscript,
      routePlannerRevision,
      isAnswerToEdenQuestion,
    ]
  );

  const greetUser = useCallback(async () => {
    const msg = firstName
      ? `I'm listening, ${firstName}.`
      : "I'm listening.";
    // The greeting answers nothing: a question Eden asked just before it is
    // still the one a short spoken reply answers.
    const questionWasLatest = edenQuestionTurnRef.current === `turn-${conversationTurnSequenceRef.current}`;
    setLastAssistantReply(msg);
    if (questionWasLatest) edenQuestionTurnRef.current = `turn-${conversationTurnSequenceRef.current}`;
    setLastAssistantVisualizations([]);
    setVoicePhase("ready");
  }, [firstName, setLastAssistantReply]);

  const startListening = useCallback(() => {
    stopSpeaking();
    setIsListening(true);
    setVoicePhase("listening");
  }, [stopSpeaking]);
  const stopListening = useCallback(() => {
    setIsListening(false);
    setVoicePhase((prev) =>
      prev === "processing" || prev === "speaking" || prev === "error" ? prev : "idle"
    );
  }, []);

  // Setters handed to the controller belong to the Eden session they were
  // handed out in: a reply, outcome or work update that lands after that
  // session ended is dropped instead of reaching the next account.
  const sessionBoundSetters = useMemo(() => {
    const sessionGeneration = edenSessionGeneration;
    const current = () => isCurrentEdenSession(sessionGeneration);
    return {
      setLastAssistantReply: (value) => {
        if (current()) setLastAssistantReply(value);
      },
      setLastAssistantVisualizations: (value) => {
        if (current()) setLastAssistantVisualizations(value);
      },
      setDelegatedWork: (value) => {
        if (current()) setDelegatedWork(value);
      },
    };
  }, [edenSessionGeneration, setLastAssistantReply]);

  const value = useMemo(
    () => ({
      isListening,
      isSending,
      isThinking,
      isProcessing,
      isAwaitingReply: awaitingReplyCount > 0,
      isSpeaking,
      dictationMode,
      voicePhase,
      interimTranscript,
      voiceOutputStatus,
      lastUserUtterance,
      lastAssistantReply,
      lastAssistantVisualizations,
      delegatedWork,
      setDelegatedWork: sessionBoundSetters.setDelegatedWork,
      conversationTurns,
      lastGovernedChange,
      edenSessionGeneration,

      role,
      sherlocSystemPrompt,
      firstName,

      // ✅ Eden context plumbing
      edenContext,
      setEdenContext,
      clearVoiceOutputStatus,

      startListening,
      stopListening,
      handleFinalTranscript,
      isAnswerToEdenQuestion,
      greetUser,
      setLastAssistantReply: sessionBoundSetters.setLastAssistantReply,
      setLastAssistantVisualizations: sessionBoundSetters.setLastAssistantVisualizations,
      setVoicePhase,
      setInterimTranscript,
      clearInterimTranscript,
    }),
    [
      isListening,
      isSending,
      isThinking,
      isProcessing,
      awaitingReplyCount,
      isSpeaking,
      dictationMode,
      voicePhase,
      interimTranscript,
      voiceOutputStatus,
      lastUserUtterance,
      lastAssistantReply,
      lastAssistantVisualizations,
      delegatedWork,
      conversationTurns,
      lastGovernedChange,
      edenSessionGeneration,
      sessionBoundSetters,
      role,
      sherlocSystemPrompt,
      firstName,
      edenContext,
      clearVoiceOutputStatus,
      startListening,
      stopListening,
      handleFinalTranscript,
      isAnswerToEdenQuestion,
      greetUser,
      clearInterimTranscript,
    ]
  );

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

export function useSherlocVoice() {
  const ctx = useContext(VoiceContext);
  if (!ctx) {
    throw new Error("useSherlocVoice must be used inside SherlocVoiceProvider");
  }
  return ctx;
}
